import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { cn } from '@/components/ui/utils';
import { useTaskDetail } from '../hooks/useTaskDetail';
import { InternBadge } from './shared/Badge';
import { NoteThread } from './NoteThread';
import { BlockerComposer } from './BlockerComposer';
import { DetailSkeleton } from './shared/InternSkeletons';
import { InternErrorState } from './shared/InternEmptyState';
import type { TaskNote } from '../types';

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const composerRef = useRef<HTMLDivElement>(null);

  const { task, notes, loading, error, updateStatus, addNote, resolveBlocker, refresh } =
    useTaskDetail(taskId);

  // Auto-scroll to composer if ?focus=blocker
  useEffect(() => {
    if (!loading && searchParams.get('focus') === 'blocker' && composerRef.current) {
      composerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, searchParams]);

  const escalation = task?.status === 'done' ? 'done' : (task?.escalation ?? 'regular');

  // Find unresolved blocker notes that have been answered
  const unresolvedBlockerWithAnswer: TaskNote | null = (() => {
    if (!task || task.status !== 'blocked') return null;
    const blocker = [...notes].reverse().find((n) => n.is_blocker && !n.resolved);
    if (!blocker) return null;
    const answer = notes.find((n) => n.created_at > blocker.created_at && n.author !== 'worker');
    return answer ? blocker : null;
  })();

  const accentColor =
    escalation === 'red' ? 'var(--destructive)' :
    escalation === 'orange' ? 'var(--color-gold)' :
    'var(--color-forest)';

  return (
    <div
      className="min-h-screen"
      style={{ background: 'var(--color-warm-white)' }}
    >
      {/* Mini top bar for detail pages */}
      <div className="sticky top-0 z-20 border-b bg-white px-6 py-3 flex items-center gap-4">
        <button
          onClick={() => navigate('/intern/dashboard')}
          className="text-[14px] font-[500] text-[var(--color-forest)] hover:underline flex items-center gap-1"
          aria-label="Back to dashboard"
        >
          ← Today
        </button>
      </div>

      <main className="mx-auto max-w-[880px] px-6 py-10">
        {loading && <DetailSkeleton />}
        {error && <InternErrorState onRetry={refresh} />}

        {!loading && !error && task && (
          <div className="space-y-8">
            {/* Header */}
            <div
              className="rounded-[4px] border bg-white p-6"
              style={{
                borderColor: escalation === 'regular' ? 'rgba(27,58,45,0.15)' : 'rgba(154,41,41,0.2)',
                borderLeft: `4px solid ${accentColor}`,
              }}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <h1
                  className="font-serif font-[600] text-[26px] leading-snug flex-1"
                  style={{ fontFamily: 'var(--font-serif)', color: 'var(--color-near-black)' }}
                >
                  {task.title}
                </h1>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {task.is_critical && (
                    <InternBadge variant="critical" dot={false}>Critical</InternBadge>
                  )}
                  {task.status === 'blocked' && (
                    <InternBadge variant="blocked">Blocked</InternBadge>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="flex flex-wrap gap-6 mb-4">
                {task.due_date && (
                  <div>
                    <p className="text-[12px] font-[500] uppercase tracking-wide text-[var(--color-mid-gray)] mb-0.5">Due</p>
                    <p
                      className="text-[16px] font-[600]"
                      style={{ color: escalation === 'red' ? 'var(--destructive)' : 'var(--color-near-black)' }}
                    >
                      {formatDate(task.due_date)}
                      {task.due_in_days != null && task.status !== 'done' && (
                        <span
                          className="ml-2 font-serif text-[20px]"
                          style={{ color: escalation === 'red' ? 'var(--destructive)' : 'var(--color-gold-dark)' }}
                        >
                          {task.due_in_days < 0
                            ? `${Math.abs(task.due_in_days)}d overdue`
                            : task.due_in_days === 0
                            ? 'due today'
                            : `in ${task.due_in_days}d`}
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {task.go_live_date && (
                  <div>
                    <p className="text-[12px] font-[500] uppercase tracking-wide text-[var(--color-mid-gray)] mb-0.5">Goes live</p>
                    <p className="text-[16px] font-[600] text-[var(--color-near-black)]">
                      {formatDate(task.go_live_date)}
                    </p>
                  </div>
                )}
              </div>

              {/* Status control */}
              <div className="flex flex-wrap gap-2">
                {(['not_started', 'in_progress', 'done'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(s)}
                    className={cn(
                      'px-3 py-1.5 rounded-[4px] text-[13px] font-[500] border transition-all duration-200',
                      task.status === s
                        ? 'bg-[var(--color-forest)] text-[var(--color-warm-white)] border-[var(--color-forest)]'
                        : 'bg-white text-[var(--color-charcoal)] border-[var(--border)] hover:border-[var(--color-forest)]'
                    )}
                    aria-pressed={task.status === s}
                  >
                    {s === 'not_started' ? 'Not started' : s === 'in_progress' ? 'In progress' : 'Done'}
                  </button>
                ))}
              </div>
            </div>

            {/* Full description */}
            {task.description && (
              <div>
                <h2 className="text-[16px] font-[600] text-[var(--color-near-black)] mb-2">Description</h2>
                <p className="text-[16px] text-[var(--color-charcoal)] leading-relaxed whitespace-pre-wrap">
                  {task.description}
                </p>
              </div>
            )}

            {/* Meta block */}
            {(task.downstream_owner || task.time_estimate_min || (task.links && task.links.length > 0)) && (
              <div className="rounded-[4px] border border-[var(--border)] bg-white p-5 space-y-3">
                {task.downstream_owner && (
                  <div>
                    <span className="text-[13px] font-[500] text-[var(--color-mid-gray)] uppercase tracking-wide">
                      When done →
                    </span>{' '}
                    <span className="text-[14px] text-[var(--color-charcoal)]">{task.downstream_owner}</span>
                  </div>
                )}
                {task.time_estimate_min && (
                  <div>
                    <span className="text-[13px] font-[500] text-[var(--color-mid-gray)] uppercase tracking-wide">
                      Estimated
                    </span>{' '}
                    <span className="text-[14px] text-[var(--color-charcoal)]">~{task.time_estimate_min} min</span>
                  </div>
                )}
                {task.links && task.links.length > 0 && (
                  <div>
                    <p className="text-[13px] font-[500] text-[var(--color-mid-gray)] uppercase tracking-wide mb-2">
                      Links & assets
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {task.links.map((link) => (
                        <a
                          key={link.url}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-[4px] text-[13px] font-[500] border bg-[var(--color-warm-white)] border-[var(--border)] text-[var(--color-forest)] hover:underline"
                        >
                          {link.label} ↗
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notes & blocker thread */}
            <div ref={composerRef}>
              <h2 className="text-[16px] font-[600] text-[var(--color-near-black)] mb-4">Notes & blockers</h2>
              <NoteThread notes={notes} />
              <div className="mt-4">
                <BlockerComposer
                  onAddNote={addNote}
                  onResolveBlocker={unresolvedBlockerWithAnswer ? resolveBlocker : undefined}
                  resolvedNoteId={unresolvedBlockerWithAnswer?.id ?? null}
                  disabled={task.status === 'done'}
                />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
